/**
 * Standards-based Web Push without any npm push library:
 *
 * - VAPID (RFC 8292): ES256 JWT signed with the application server's P-256 key.
 * - Message encryption (RFC 8291 + RFC 8188 `aes128gcm`): ECDH + HKDF + AES-128-GCM.
 * - Declarative Web Push (WebKit / Safari 18.4+) JSON payload shape, which the service
 *   worker also understands on Chromium.
 * - Signed, expiring action tokens so notification buttons (Done / Snooze) can act
 *   without opening the app.
 *
 * Only WebCrypto (`globalThis.crypto.subtle`), `TextEncoder`/`TextDecoder` and `URL` are
 * used, so this runs unchanged in Deno (Edge Functions), browsers and Node/Vitest.
 */

// ---------------------------------------------------------------------------
// Byte helpers
// ---------------------------------------------------------------------------

const B64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

const B64_LOOKUP: Int16Array = (() => {
  const table = new Int16Array(128).fill(-1);
  for (let i = 0; i < B64URL_ALPHABET.length; i++) table[B64URL_ALPHABET.charCodeAt(i)] = i;
  // Tolerate standard base64 too; some stores keep keys that way.
  table['+'.charCodeAt(0)] = 62;
  table['/'.charCodeAt(0)] = 63;
  return table;
})();

function subtle() {
  return globalThis.crypto.subtle;
}

/**
 * Fresh, ArrayBuffer-backed copy. WebCrypto's `BufferSource` (DOM lib, TS ≥ 5.7) rejects
 * `Uint8Array<ArrayBufferLike>`, so every buffer handed to `crypto.subtle` goes through here.
 */
function copyBytes(src: ArrayLike<number>) {
  return new Uint8Array(src);
}

function utf8(s: string) {
  return copyBytes(new TextEncoder().encode(s));
}

function concatBytes(...parts: ArrayLike<number>[]) {
  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

/** Constant-time comparison (time depends only on the lengths, never on the contents). */
function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  let diff = a.length ^ b.length;
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i++) diff |= (a[i] ?? 0) ^ (b[i] ?? 0);
  return diff === 0;
}

/** base64url without padding (RFC 4648 §5), as used by JWT, VAPID and the Push API. */
export function base64UrlEncode(bytes: Uint8Array): string {
  let out = '';
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i]! << 16) | (bytes[i + 1]! << 8) | bytes[i + 2]!;
    out +=
      B64URL_ALPHABET.charAt((n >> 18) & 63) +
      B64URL_ALPHABET.charAt((n >> 12) & 63) +
      B64URL_ALPHABET.charAt((n >> 6) & 63) +
      B64URL_ALPHABET.charAt(n & 63);
  }
  const rest = bytes.length - i;
  if (rest > 0) {
    const n = (bytes[i]! << 16) | (rest === 2 ? bytes[i + 1]! << 8 : 0);
    out += B64URL_ALPHABET.charAt((n >> 18) & 63) + B64URL_ALPHABET.charAt((n >> 12) & 63);
    if (rest === 2) out += B64URL_ALPHABET.charAt((n >> 6) & 63);
  }
  return out;
}

/**
 * Decodes base64url (padding optional). Standard base64 `+`/`/` are accepted as well.
 * Throws on any other character or an impossible length.
 */
export function base64UrlDecode(s: string) {
  const clean = s.replace(/=+$/, '');
  if (clean.length % 4 === 1) throw new Error('Invalid base64url string (bad length)');
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let buffer = 0;
  let bits = 0;
  let j = 0;
  for (let i = 0; i < clean.length; i++) {
    const code = clean.charCodeAt(i);
    const value = code < 128 ? B64_LOOKUP[code]! : -1;
    if (value < 0) throw new Error('Invalid base64url string (bad character)');
    buffer = (buffer << 6) | value;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[j++] = (buffer >> bits) & 0xff;
      buffer &= (1 << bits) - 1;
    }
  }
  return out;
}

function assertUncompressedP256(key: Uint8Array, what: string): void {
  if (key.length !== 65 || key[0] !== 0x04) {
    throw new Error(`${what} must be an uncompressed P-256 public key (65 bytes starting 0x04)`);
  }
}

async function hkdf(salt: Uint8Array, ikm: Uint8Array, info: Uint8Array, length: number) {
  const key = await subtle().importKey('raw', copyBytes(ikm), 'HKDF', false, ['deriveBits']);
  const bits = await subtle().deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: copyBytes(salt), info: copyBytes(info) },
    key,
    length * 8,
  );
  return new Uint8Array(bits);
}

/**
 * Minimal structural JWK type. `JsonWebKey` is a DOM-lib name that does not exist under the
 * core package's ES-only lib; both the DOM and Node `JsonWebKey` types are assignable to this.
 */
export interface JsonWebKeyLike {
  kty?: string;
  crv?: string;
  x?: string;
  y?: string;
  d?: string;
}

/** Builds an EC P-256 private JWK from a raw uncompressed public key and the scalar `d`. */
function p256PrivateJwk(publicKey: Uint8Array, d: string) {
  return {
    kty: 'EC',
    crv: 'P-256',
    x: base64UrlEncode(publicKey.subarray(1, 33)),
    y: base64UrlEncode(publicKey.subarray(33, 65)),
    d,
    ext: true,
  };
}

// ---------------------------------------------------------------------------
// VAPID (RFC 8292)
// ---------------------------------------------------------------------------

/** base64url raw uncompressed 65-byte public key; base64url 32-byte private scalar `d`. */
export interface VapidKeys {
  publicKey: string;
  privateKey: string;
}

/** Generates a fresh VAPID key pair (P-256). Normally done once with `pnpm vapid:generate`. */
export async function generateVapidKeys(): Promise<VapidKeys> {
  const pair = await subtle().generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, true, [
    'sign',
    'verify',
  ]);
  const publicKey = new Uint8Array(await subtle().exportKey('raw', pair.publicKey));
  const jwk = await subtle().exportKey('jwk', pair.privateKey);
  return { publicKey: base64UrlEncode(publicKey), privateKey: jwk.d! };
}

async function importVapidSigningKey(keys: VapidKeys) {
  const publicKey = base64UrlDecode(keys.publicKey);
  assertUncompressedP256(publicKey, 'VAPID public key');
  const d = base64UrlDecode(keys.privateKey);
  if (d.length !== 32) throw new Error('VAPID private key must be a 32-byte P-256 scalar');
  return subtle().importKey(
    'jwk',
    p256PrivateJwk(publicKey, base64UrlEncode(d)),
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign'],
  );
}

const DEFAULT_JWT_TTL_SECONDS = 12 * 60 * 60;
const MAX_JWT_TTL_SECONDS = 24 * 60 * 60;

/**
 * ES256 VAPID JWT. `audience` is reduced to its origin; `subject` must be a `mailto:` or
 * `https:` contact URI (Apple rejects tokens without one). Expiry defaults to 12 h and is
 * capped at 24 h, the maximum push services accept.
 */
export async function createVapidJwt(
  audience: string,
  subject: string,
  keys: VapidKeys,
  opts: { expiresInSeconds?: number; now?: Date } = {},
): Promise<string> {
  const aud = new URL(audience).origin;
  if (!/^(mailto:|https:)/.test(subject)) {
    throw new Error('VAPID subject must be a mailto: or https: URI');
  }
  const ttl = opts.expiresInSeconds ?? DEFAULT_JWT_TTL_SECONDS;
  if (!Number.isFinite(ttl) || ttl <= 0) throw new Error('expiresInSeconds must be positive');
  const nowSeconds = Math.floor((opts.now ?? new Date()).getTime() / 1000);
  const exp = nowSeconds + Math.min(Math.floor(ttl), MAX_JWT_TTL_SECONDS);

  const header = base64UrlEncode(utf8(JSON.stringify({ typ: 'JWT', alg: 'ES256' })));
  const claims = base64UrlEncode(utf8(JSON.stringify({ aud, exp, sub: subject })));
  const signingInput = `${header}.${claims}`;
  const key = await importVapidSigningKey(keys);
  // WebCrypto ECDSA already returns the JWS form: raw r || s, 64 bytes.
  const signature = await subtle().sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    key,
    utf8(signingInput),
  );
  return `${signingInput}.${base64UrlEncode(new Uint8Array(signature))}`;
}

/** `Authorization` header value: `vapid t=<jwt>, k=<public key>`; audience = endpoint origin. */
export async function vapidAuthorizationHeader(
  endpoint: string,
  subject: string,
  keys: VapidKeys,
  now?: Date,
): Promise<string> {
  const jwt = await createVapidJwt(endpoint, subject, keys, now ? { now } : {});
  return `vapid t=${jwt}, k=${base64UrlEncode(base64UrlDecode(keys.publicKey))}`;
}

// ---------------------------------------------------------------------------
// Payload encryption (RFC 8291 / RFC 8188 aes128gcm)
// ---------------------------------------------------------------------------

/** A browser `PushSubscription.toJSON()` flattened: keys are base64url as browsers give them. */
export interface PushSubscriptionKeys {
  endpoint: string;
  p256dh: string;
  auth: string;
}

const DEFAULT_RECORD_SIZE = 4096;
const TAG_LENGTH = 16;

/**
 * Encrypts `plaintext` for one subscription and returns the full `aes128gcm` request body:
 * `salt(16) | rs(4, big-endian) | idlen(1) = 65 | keyid = server public key(65) | ciphertext`.
 * A single record is produced (delimiter 0x02, no padding). `opts` exists only so tests can
 * reproduce the RFC 8291 vector deterministically; production calls pass nothing.
 */
export async function encryptPayload(
  plaintext: Uint8Array | string,
  sub: Pick<PushSubscriptionKeys, 'p256dh' | 'auth'>,
  opts: {
    salt?: Uint8Array;
    serverKeys?: { publicKey: Uint8Array; privateKeyJwk: JsonWebKeyLike };
    recordSize?: number;
  } = {},
): Promise<Uint8Array> {
  const data = typeof plaintext === 'string' ? utf8(plaintext) : copyBytes(plaintext);

  const uaPublic = base64UrlDecode(sub.p256dh);
  assertUncompressedP256(uaPublic, 'Subscription p256dh');
  const authSecret = base64UrlDecode(sub.auth);
  if (authSecret.length !== 16) throw new Error('Subscription auth secret must be 16 bytes');

  const salt = opts.salt
    ? copyBytes(opts.salt)
    : globalThis.crypto.getRandomValues(new Uint8Array(16));
  if (salt.length !== 16) throw new Error('Salt must be 16 bytes');

  const recordSize = opts.recordSize ?? DEFAULT_RECORD_SIZE;
  if (!Number.isInteger(recordSize) || recordSize < TAG_LENGTH + 2 || recordSize > 0xffffffff) {
    throw new Error('Invalid record size');
  }
  if (data.length + 1 + TAG_LENGTH > recordSize) {
    throw new Error('Payload too large for a single aes128gcm record');
  }

  // Application server (sender) key pair: ephemeral unless a test supplies one.
  let asPublic: Uint8Array;
  let asPrivate: Awaited<ReturnType<typeof globalThis.crypto.subtle.importKey>>;
  if (opts.serverKeys) {
    asPublic = copyBytes(opts.serverKeys.publicKey);
    assertUncompressedP256(asPublic, 'Server public key');
    const d = opts.serverKeys.privateKeyJwk.d;
    if (!d) throw new Error('Server private JWK must include d');
    asPrivate = await subtle().importKey(
      'jwk',
      p256PrivateJwk(asPublic, d),
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      ['deriveBits'],
    );
  } else {
    const pair = await subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, [
      'deriveBits',
    ]);
    asPublic = new Uint8Array(await subtle().exportKey('raw', pair.publicKey));
    asPrivate = pair.privateKey;
  }

  const uaKey = await subtle().importKey(
    'raw',
    uaPublic,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );
  const ecdhSecret = new Uint8Array(
    await subtle().deriveBits({ name: 'ECDH', public: uaKey }, asPrivate, 256),
  );

  // RFC 8291 §3.4: combine the ECDH secret with the auth secret.
  const keyInfo = concatBytes(utf8('WebPush: info\x00'), uaPublic, asPublic);
  const ikm = await hkdf(authSecret, ecdhSecret, keyInfo, 32);
  // RFC 8188 §2.2 / §2.3: content-encryption key and nonce (sequence number 0).
  const cek = await hkdf(salt, ikm, utf8('Content-Encoding: aes128gcm\x00'), 16);
  const nonce = await hkdf(salt, ikm, utf8('Content-Encoding: nonce\x00'), 12);

  const aesKey = await subtle().importKey('raw', cek, { name: 'AES-GCM' }, false, ['encrypt']);
  const record = concatBytes(data, [0x02]); // last-record padding delimiter, no padding
  const ciphertext = new Uint8Array(
    await subtle().encrypt({ name: 'AES-GCM', iv: nonce, tagLength: 128 }, aesKey, record),
  );

  const header = new Uint8Array(16 + 4 + 1 + asPublic.length);
  header.set(salt, 0);
  new DataView(header.buffer).setUint32(16, recordSize, false);
  header[20] = asPublic.length;
  header.set(asPublic, 21);
  return concatBytes(header, ciphertext);
}

// ---------------------------------------------------------------------------
// Request building (RFC 8030)
// ---------------------------------------------------------------------------

export interface WebPushRequest {
  endpoint: string;
  headers: Record<string, string>;
  body: Uint8Array;
}

export type PushUrgency = 'very-low' | 'low' | 'normal' | 'high';

/** Push services must accept at least 4096 bytes of body (RFC 8030 §7.2); more is unsafe. */
export const MAX_PUSH_BODY_BYTES = 4096;

/**
 * RFC 8030 §5.4: a Topic is at most 32 characters of the base64url alphabet. Valid topics are
 * kept as-is; anything else is replaced by a stable hash (first 24 bytes of SHA-256 → 32 chars),
 * so the same logical topic still collapses onto the same pending message.
 */
export async function sanitizePushTopic(topic: string): Promise<string> {
  if (/^[A-Za-z0-9_-]{1,32}$/.test(topic)) return topic;
  const digest = new Uint8Array(await subtle().digest('SHA-256', utf8(topic)));
  return base64UrlEncode(digest.subarray(0, 24));
}

/**
 * Builds everything needed for `fetch(req.endpoint, { method: 'POST', headers, body })`.
 * Defaults: TTL 1 day, Urgency `high` (reminders must not be deferred by battery savers).
 */
export async function buildWebPushRequest(
  sub: PushSubscriptionKeys,
  payload: string,
  vapid: VapidKeys & { subject: string },
  opts: { ttl?: number; urgency?: PushUrgency; topic?: string; now?: Date } = {},
): Promise<WebPushRequest> {
  if (new URL(sub.endpoint).protocol !== 'https:') {
    throw new Error('Push endpoint must be an https: URL');
  }
  const ttl = opts.ttl ?? 86400;
  if (!Number.isFinite(ttl) || ttl < 0) throw new Error('TTL must be a non-negative number');

  const body = await encryptPayload(payload, sub);
  if (body.length > MAX_PUSH_BODY_BYTES) {
    throw new Error(`Encrypted push body exceeds ${MAX_PUSH_BODY_BYTES} bytes`);
  }

  const headers: Record<string, string> = {
    Authorization: await vapidAuthorizationHeader(sub.endpoint, vapid.subject, vapid, opts.now),
    'Content-Encoding': 'aes128gcm',
    'Content-Type': 'application/octet-stream',
    TTL: String(Math.floor(ttl)),
    Urgency: opts.urgency ?? 'high',
  };
  if (opts.topic) headers.Topic = await sanitizePushTopic(opts.topic);
  return { endpoint: sub.endpoint, headers, body };
}

/** How to treat a push service response: delete the subscription on `gone`, back off on `retry`. */
export function classifyPushResponse(status: number): 'ok' | 'gone' | 'retry' | 'error' {
  if (status === 200 || status === 201 || status === 202) return 'ok';
  if (status === 404 || status === 410) return 'gone';
  if (status === 429 || (status >= 500 && status <= 599)) return 'retry';
  return 'error';
}

// ---------------------------------------------------------------------------
// Declarative Web Push payload (WebKit, `web_push: 8030`)
// ---------------------------------------------------------------------------

export interface DeclarativeNotification {
  title: string;
  body?: string;
  /** Absolute URL opened when the notification is tapped (`notification.navigate`). */
  url: string;
  tag?: string;
  icon?: string;
  /** App icon badge count (`app_badge`). */
  badge?: number;
  actions?: { action: string; title: string; url?: string }[];
  /** Extra fields for our service worker (e.g. reminder id, action token). */
  data?: Record<string, unknown>;
  silent?: boolean;
  requireInteraction?: boolean;
}

function absoluteHttpUrl(value: string, what: string): string {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new Error(`${what} must be an absolute URL`);
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new Error(`${what} must be an http(s) URL`);
  }
  return parsed.href;
}

/**
 * JSON for the WebKit Declarative Web Push format. Safari 18.4+ shows it without running the
 * service worker; on Chromium our service worker parses the same JSON. Everything the SW needs
 * beyond the standard members lives in `notification.data` (which always includes `url`).
 */
export function buildDeclarativePayload(n: DeclarativeNotification): string {
  if (!n.title) throw new Error('Notification title is required');
  const navigate = absoluteHttpUrl(n.url, 'Notification url');

  const notification: Record<string, unknown> = { title: n.title };
  if (n.body !== undefined) notification.body = n.body;
  notification.navigate = navigate;
  if (n.tag !== undefined) notification.tag = n.tag;
  if (n.icon !== undefined) notification.icon = n.icon;
  notification.lang = 'en';
  notification.dir = 'ltr';
  notification.silent = n.silent ?? false;
  if (n.requireInteraction !== undefined) notification.require_interaction = n.requireInteraction;
  if (n.actions && n.actions.length > 0) {
    notification.actions = n.actions.map((a) => ({
      action: a.action,
      title: a.title,
      navigate: a.url ? absoluteHttpUrl(a.url, `Action "${a.action}" url`) : navigate,
    }));
  }
  notification.data = { ...n.data, url: navigate };

  const message: Record<string, unknown> = { web_push: 8030, notification };
  if (n.badge !== undefined) {
    if (!Number.isInteger(n.badge) || n.badge < 0) {
      throw new Error('Badge must be a non-negative integer');
    }
    message.app_badge = n.badge;
  }
  return JSON.stringify(message);
}

// ---------------------------------------------------------------------------
// Action tokens (notification buttons → Edge Function, no login needed)
// ---------------------------------------------------------------------------

export interface ActionTokenPayload {
  /** Reminder id, or '' */
  r: string;
  /** Task id, or '' */
  t: string;
  /** User id */
  u: string;
  /** Expiry, unix seconds */
  exp: number;
}

async function hmacSha256(secret: string, message: string) {
  const key = await subtle().importKey(
    'raw',
    utf8(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return new Uint8Array(await subtle().sign('HMAC', key, utf8(message)));
}

function isActionTokenPayload(v: unknown): v is ActionTokenPayload {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.r === 'string' &&
    typeof o.t === 'string' &&
    typeof o.u === 'string' &&
    typeof o.exp === 'number' &&
    Number.isFinite(o.exp)
  );
}

/** `base64url(json).base64url(HMAC-SHA256(secret, base64url(json)))` */
export async function signActionToken(
  payload: ActionTokenPayload,
  secret: string,
): Promise<string> {
  if (!secret) throw new Error('Action token secret is required');
  if (!isActionTokenPayload(payload)) throw new Error('Invalid action token payload');
  const json = JSON.stringify({ r: payload.r, t: payload.t, u: payload.u, exp: payload.exp });
  const body = base64UrlEncode(utf8(json));
  return `${body}.${base64UrlEncode(await hmacSha256(secret, body))}`;
}

/** Returns the payload when the signature matches and it has not expired, otherwise `null`. */
export async function verifyActionToken(
  token: string,
  secret: string,
  now: Date = new Date(),
): Promise<ActionTokenPayload | null> {
  if (!secret || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 2) return null;
  const [body, signature] = parts as [string, string];

  let parsed: unknown;
  try {
    const given = base64UrlDecode(signature);
    const expected = await hmacSha256(secret, body);
    if (!timingSafeEqual(given, expected)) return null;
    parsed = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(base64UrlDecode(body)));
  } catch {
    return null;
  }
  if (!isActionTokenPayload(parsed)) return null;
  if (parsed.exp * 1000 <= now.getTime()) return null;
  return { r: parsed.r, t: parsed.t, u: parsed.u, exp: parsed.exp };
}
